import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { TrendingUp, AlertCircle } from 'lucide-react';
import { useAuth } from '../App';
import { apiGet, apiPost } from '../utils/apiRequest';

const ResultsCheckup = () => {
  const { checkupId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [checkup, setCheckup] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!user) {
      navigate('/login');
      return;
    }

    // Fetch and attach checkup to user
    fetchAndAttachCheckup();
  }, [user, checkupId]);

  const fetchAndAttachCheckup = async () => {
    if (!checkupId) {
      setError('No checkup ID provided');
      setIsLoading(false);
      return;
    }

    try {
      // First, attach checkup to user (idempotent)
      await apiPost(`/api/checkups/${checkupId}/attach`, {});
      
      // Then fetch checkup data
      const checkupData = await apiGet(`/api/checkups/${checkupId}`);
      setCheckup(checkupData);
      setIsLoading(false);
    } catch (err) {
      console.error('Error fetching checkup:', err);
      setError(err.message || 'Failed to load checkup. Please try again or contact support@finance-vitals.com');
      setIsLoading(false);
    }
  };

  const handleViewDashboard = () => {
    navigate('/individual');
  };

  const handleRetry = () => {
    setError('');
    setIsLoading(true);
    fetchAndAttachCheckup();
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100">
        {/* Header */}
        <header className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <TrendingUp className="h-8 w-8 text-green-600" />
              <span className="text-2xl font-bold text-gray-900">Finance Vitals</span>
            </div>
          </div>
        </header>

        {/* Error Content */}
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="text-center mb-8">
              <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Unable to Load Results</h1>
              <p className="text-gray-600 text-lg">{error}</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={handleRetry}
                className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
              >
                Try Again
              </button>
              <Link
                to="/individual"
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold rounded-lg transition-colors text-center"
              >
                Go to Dashboard
              </Link>
            </div>

            <p className="text-sm text-gray-500 text-center mt-6">
              Need help? Email{' '}
              <a href="mailto:support@finance-vitals.com" className="text-blue-600 hover:underline" data-testid="results-support-email-link">
                support@finance-vitals.com
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
            <TrendingUp className="h-8 w-8 text-green-600" />
            <span className="text-2xl font-bold text-gray-900">Finance Vitals</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {isLoading ? (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-6"></div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Saving Your Results...</h2>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="text-center mb-8">
              <div className="text-green-600 text-6xl mb-4">✓</div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Checkup Saved!</h1>
              <p className="text-gray-600 text-lg mb-4">
                Your financial health score is now saved to your account.
              </p>
              
              {/* Display actual checkup data */}
              {checkup && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                  <div className="flex items-center justify-center gap-4 mb-4">
                    <div className="text-center">
                      <div className="text-4xl font-bold text-gray-900">
                        {checkup.score_breakdown?.total_score || 0}/100
                      </div>
                      <div className="text-sm text-gray-600 mt-1">Your Score</div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    Saved {new Date(checkup.created_at).toLocaleDateString()} at{' '}
                    {new Date(checkup.created_at).toLocaleTimeString()}
                  </p>
                </div>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-gray-900 mb-2">What's Next?</h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>View your complete analysis in your dashboard</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-600 mr-2">✓</span>
                  <span>Track your financial health over time with future checkups</span>
                </li>
                {checkup?.can_offer_deep_analysis && (
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    <span>Unlock Deep Analysis for detailed insights and action plans</span>
                  </li>
                )}
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={handleViewDashboard}
                className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
              >
                Go to Dashboard
              </button>
              <button
                onClick={() => navigate('/free-checkup')}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold rounded-lg transition-colors"
              >
                Run Another Checkup
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResultsCheckup;
